/**
 * Input Component
 * Reusable input component with various states and styles
 */

import { forwardRef } from 'react';
import styles from './Input.module.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | boolean;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  variant?: 'default' | 'search';
  inputText?: string;
    stubErrorSpace?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ 
        label, 
        error, 
        helperText = '',
        leftIcon, 
        rightIcon, 
        fullWidth = false, 
        variant = 'default',
        className = '',
        inputText,
        stubErrorSpace,
        style,
        ...props
    }, ref) => {
        const inputClasses = [
            styles.input,
            styles[`input__${variant}`],
            leftIcon && styles.input__withLeftIcon,
            rightIcon && styles.input__withRightIcon,
            error && styles.input__error,
            fullWidth && styles.input__fullWidth,
            inputText && styles.input__withInputText,
            className
        ].filter(Boolean).join(' ');

        return (
            <div className={`${styles.container} ${fullWidth ? styles.container__fullWidth : ''}`}>
                {label && (
                    <label className={styles.label}>
                        {label}
                    </label>
                )}
        
                <div className={styles.inputWrapper}>
                    {leftIcon && (
                        <div className={styles.leftIcon}>
                            {leftIcon}
                        </div>
                    )}

                    {inputText && (
                        <div className={styles.inputTextPrefix}>
                            {inputText}
                        </div>
                    )}

                    <input
                        ref={ref}
                        className={inputClasses}
                        style={style}
                        {...props}
                    />
          
                    {rightIcon && (
                        <div className={styles.rightIcon}>
                            {rightIcon}
                        </div>
                    )}
                </div>

                {(typeof error === 'string' || helperText || stubErrorSpace) && (
                    <div className={`${styles.message} ${error ? styles.message__error : ''}`}>
                        {typeof error === 'string' ? error : helperText}
                    </div>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
